import {
    clearCaseDbCsrfToken,
    ensureCaseDbCsrfToken,
    getCaseDbCsrfToken,
    withCsrfHeaders,
} from '../utils/csrf';
import {
    DocumentUploadOutcomeError,
    documentUploadResponseError,
    parseDocumentUploadResponse,
    unknownDocumentUploadError,
} from '../utils/documentUploadOutcome';

const formatUploadBytes = (bytes) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }
    const precision = value >= 10 || unitIndex === 0 ? 0 : 1;
    return `${value.toFixed(precision)} ${units[unitIndex]}`;
};

const S3_UPLOAD_ENDPOINT = 'https://api.casedb.co/s3_upload.php';
const UPLOAD_CONFLICT_POLICY_VERSION = 1;
const SKIPPED_UPLOAD_MESSAGE = 'Skipped — already exists';
const uploadDisplayName = (name, caseName) => String(name).replace(
    /\{\{Name\}\}/g, () => caseName || 'Case documents',
);
const skippedUploadResult = fileData => ({
    status: 'fulfilled',
    value: {
        success: true, skipped: true, skip_reason: 'destination_exists', files: [],
        file_name: fileData.file.name, upload_conflict_policy_version: UPLOAD_CONFLICT_POLICY_VERSION,
    },
});
const uploadRelativePath = (fileData) => {
    const directory = fileData.relativePath?.includes('/')
        ? fileData.relativePath.split('/').slice(0, -1).join('/') : '';
    // Multipart uses the native File name, not the optional display label.
    return directory ? `${directory}/${fileData.file.name}` : fileData.file.name;
};
const uploadFolderPath = (fileData, folderName) => (
    fileData.relativePath?.includes('/')
        ? `${folderName}/${fileData.relativePath.split('/').slice(0, -1).join('/')}`
        : folderName
);
const existingUploadCheckError = (status = 0) => new DocumentUploadOutcomeError(
    'CaseDB could not safely check for existing files. No files in this selection were sent. Retry the check before uploading.',
    { status, uploadOutcome: 'rejected', retryable: true },
);
const FOLDER_RENAME_TERMINAL_FAILURES = new Set([
    'failed',
    'error',
    'cancelled',
    'canceled',
    'manual_review',
    'review_required',
    'needs_review'
]);

class UploadApiError extends Error {
    constructor(message, status = 0) {
        super(message);
        this.name = 'UploadApiError';
        this.status = status;
    }
}

// Simplified upload service using S3's native multipart upload
export class SimpleUploadService {
    constructor() {
        this.uploads = new Map();
        this.listeners = new Set();
        // File bodies begin together for useful aggregate throughput. The API
        // queues only their final authoritative S3/database commit behind the
        // case mutation gate, so sibling uploads do not fail one another.
        this.MAX_CONCURRENT_UPLOADS = 3;
        this.FOLDER_RENAME_POLL_INTERVAL_MS = 2000;
        this.folderRenamePromises = new Map();
    }

    addListener(callback) {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }

    notifyListeners() {
        const currentUploads = Array.from(this.uploads.values());
        this.listeners.forEach(callback => callback(currentUploads));
    }

    async uploadFiles(files, options = {}) {
        const {
            caseId, folderName = '{{Name}}', userId, onProgress, uploadContext, skipExisting = false,
            confirmedExistingIndexes, caseName,
        } = options;
        
        if (!caseId) {
            throw new DocumentUploadOutcomeError('Case ID is required', { uploadOutcome: 'rejected', retryable: true });
        }

        const uploadList = Array.from(files || []);
        const results = new Array(uploadList.length);
        const selection = uploadList.map((fileData, index) => ({
            fileData, index, skipExisting: skipExisting === true || fileData.skipExisting === true,
        }));
        const confirmed = new Set();
        if (skipExisting === true && confirmedExistingIndexes !== undefined) {
            const valid = confirmedExistingIndexes instanceof Set
                && [...confirmedExistingIndexes].every(id => typeof id === 'string'
                    && /^(0|[1-9]\d*)$/.test(id) && Number.isSafeInteger(Number(id))
                    && Number(id) < selection.length);
            if (!valid) {
                const rejected = selection.map(({ fileData }) => {
                    const result = { status: 'rejected', reason: existingUploadCheckError() };
                    this.recordUnsentUpload(fileData, uploadContext, result, caseName);
                    return result;
                });
                if (selection.length > 0) this.notifyListeners();
                return rejected;
            }
            confirmedExistingIndexes.forEach(id => confirmed.add(id));
        }
        const checkedSelection = selection.filter(item => item.skipExisting && !confirmed.has(String(item.index)));
        const existing = new Set(confirmed);
        // Complete every chunk before sending any file body. A partial or old
        // server response cannot silently turn this into an overwrite upload.
        if (checkedSelection.length > 0) {
            try {
                const matches = await this.checkExistingUploads(checkedSelection, { caseId, folderName });
                matches.forEach(id => existing.add(id));
            } catch (error) {
                const reason = error instanceof DocumentUploadOutcomeError ? error : existingUploadCheckError();
                const settled = selection.map(({ fileData, index }) => {
                    const result = confirmed.has(String(index)) ? skippedUploadResult(fileData) : { status: 'rejected', reason };
                    this.recordUnsentUpload(fileData, uploadContext, result, caseName);
                    if (result.status === 'fulfilled') onProgress?.(fileData.file, 100);
                    return result;
                });
                this.notifyListeners();
                return settled;
            }
        }
        const queue = selection.filter(({ fileData, index }) => {
            if (!existing.has(String(index))) return true;
            const result = skippedUploadResult(fileData);
            results[index] = result;
            this.recordUnsentUpload(fileData, uploadContext, result, caseName);
            onProgress?.(fileData.file, 100);
            return false;
        });
        if (existing.size > 0) this.notifyListeners();
        const workerCount = Math.min(this.MAX_CONCURRENT_UPLOADS, queue.length);

        const uploadNext = async () => {
            while (queue.length > 0) {
                const { fileData, index, skipExisting: shouldSkipExisting } = queue.shift();
                try {
                    const result = await this.uploadSingleFile(fileData, {
                        caseId,
                        folderName: uploadFolderPath(fileData, folderName),
                        userId,
                        onProgress,
                        uploadContext,
                        skipExisting: shouldSkipExisting,
                        caseName,
                    });
                    results[index] = result;
                } catch (error) {
                    results[index] = {
                        status: 'rejected',
                        reason: error instanceof DocumentUploadOutcomeError
                            ? error : unknownDocumentUploadError(),
                    };
                }
            }
        };

        await Promise.all(Array.from({ length: workerCount }, uploadNext));
        return results;
    }

    // Read-only modal preview: no tray entries, storage writes, or file bodies.
    // IDs are stable positions in this exact caller-owned selection snapshot.
    async checkExistingFiles(files, { caseId, folderName = '{{Name}}', signal, onCheckProgress } = {}) {
        if (!caseId) throw existingUploadCheckError();
        const selection = Array.from(files || []).map((fileData, index) => ({ fileData, index }));
        if (selection.length === 0) return new Set();
        return this.checkExistingUploads(selection, { caseId, folderName, signal, onCheckProgress });
    }

    async checkExistingUploads(selection, { caseId, folderName, signal, onCheckProgress }) {
        const existing = new Set();
        let status = 0;
        try {
            if (signal?.aborted) throw existingUploadCheckError();
            if (!await ensureCaseDbCsrfToken()) throw existingUploadCheckError();
            for (let offset = 0; offset < selection.length; offset += 100) {
                if (signal?.aborted) throw existingUploadCheckError();
                const chunk = selection.slice(offset, offset + 100);
                const expectedIds = new Set(chunk.map(({ index }) => String(index)));
                const response = await fetch(S3_UPLOAD_ENDPOINT, {
                    method: 'POST', credentials: 'include', cache: 'no-store',
                    ...(signal ? { signal } : {}),
                    headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                    body: JSON.stringify({
                        action: 'check_upload_existing', case_id: caseId, folder_path: folderName,
                        files: chunk.map(({ fileData, index }) => ({
                            id: String(index), relative_path: uploadRelativePath(fileData),
                        })),
                    }),
                });
                status = response.status;
                const result = await response.json();
                if (signal?.aborted) throw existingUploadCheckError(status);
                if (!response.ok || result?.success !== true
                    || result.upload_conflict_policy_version !== UPLOAD_CONFLICT_POLICY_VERSION
                    || !Array.isArray(result.files) || result.files.length !== chunk.length) {
                    throw existingUploadCheckError(status);
                }
                for (const file of result.files) {
                    if (!file || typeof file.id !== 'string' || !expectedIds.delete(file.id)
                        || typeof file.exists !== 'boolean') throw existingUploadCheckError(status);
                    if (file.exists) existing.add(file.id);
                }
                onCheckProgress?.({ completed: offset + chunk.length, total: selection.length });
                if (signal?.aborted) throw existingUploadCheckError(status);
            }
            return existing;
        } catch (error) {
            throw existingUploadCheckError(status);
        }
    }

    recordUnsentUpload(fileData, uploadContext, result, caseName) {
        const skipped = result.status === 'fulfilled';
        const upload = {
            id: `upload-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            fileName: uploadDisplayName(fileData.relativePath || fileData.fileName || fileData.file.name, caseName),
            fileSize: fileData.file.size, createdAt: Date.now(), operation: 'upload',
            status: skipped ? 'completed' : 'error', progress: skipped ? 100 : 0,
            skipped, statusMessage: skipped ? SKIPPED_UPLOAD_MESSAGE : result.reason.message,
            error: skipped ? null : result.reason.message,
        };
        this.uploads.set(upload.id, upload);
        uploadContext?.dispatch?.({ type: 'START_UPLOAD', payload: upload });
        uploadContext?.dispatch?.({
            type: skipped ? 'COMPLETE_UPLOAD' : 'FAIL_UPLOAD',
            payload: {
                id: upload.id, skipped, statusMessage: upload.statusMessage, error: upload.error,
            },
        });
        // The caller publishes this synchronous group once. Notifying for
        // every skipped row makes case listeners refresh the same folder once
        // per file even though none of those files were newly written.
    }

    async uploadSingleFile(fileData, options) {
        const { file, fileName } = fileData;
        const { caseId, folderName, userId, onProgress, uploadContext, skipExisting = false, caseName } = options;
        
        const uploadId = `upload-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        
        const upload = {
            id: uploadId,
            fileName: uploadDisplayName(fileName || file.name, caseName),
            status: 'uploading',
            progress: 0,
            error: null,
            fileSize: file.size,
            createdAt: Date.now(),
            operation: 'upload'
        };

        // Track in local Map (for backward compatibility)
        this.uploads.set(uploadId, upload);
        
        // Also track in global upload context if available
        if (uploadContext?.dispatch) {
            uploadContext.dispatch({
                type: 'START_UPLOAD',
                payload: {
                    id: uploadId,
                    fileName: uploadDisplayName(fileName || file.name, caseName),
                    fileSize: file.size,
                    operation: 'upload',
                    statusMessage: 'Starting upload'
                }
            });
        }
        this.notifyListeners();

        // Pre-fetch the CSRF token so the global XHR interceptor can attach it.
        try {
            await ensureCaseDbCsrfToken();
        } catch (error) {
            // The guarded request will surface any real auth error.
        }

        return new Promise((resolve) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('case_id', caseId);
            formData.append('folder_path', folderName);
            formData.append('user_id', userId);
            if (skipExisting === true) formData.append('conflict_policy', 'skip_existing');

            const sendAttempt = (csrfRetryAttempted = false, verifiedRetryToken = '') => {
                const xhr = new XMLHttpRequest();

                // Progress tracking
                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable) {
                        const progress = Math.round((event.loaded / event.total) * 100);
                        const uploadedText = formatUploadBytes(event.loaded);
                        const totalText = formatUploadBytes(event.total);
                        const statusMessage = progress >= 100
                            ? 'Finalizing upload on server...'
                            : `${progress}% (${uploadedText} of ${totalText})`;

                        upload.progress = progress;
                        upload.statusMessage = statusMessage;
                        this.uploads.set(uploadId, upload);
                        this.notifyListeners();

                        // Update global upload context
                        if (uploadContext?.dispatch) {
                            uploadContext.dispatch({
                                type: 'UPDATE_PROGRESS',
                                payload: {
                                    id: uploadId,
                                    progress,
                                    statusMessage,
                                    loadedBytes: event.loaded,
                                    totalBytes: event.total,
                                    updatedAt: Date.now()
                                }
                            });
                        }

                        // Call the callback if provided
                        if (onProgress) {
                            onProgress(file, progress);
                        }
                    }
                });

                xhr.addEventListener('load', async () => {
                    try {
                        const result = parseDocumentUploadResponse(xhr.responseText, xhr.status);

                        // This exact response comes from the pre-write CSRF gate.
                        // It is safe to resend once after verifying the same user;
                        // a generic 403 or uncertain upload result is not retryable.
                        if (!csrfRetryAttempted
                            && xhr.status === 403
                            && result?.success === false
                            && result.message === 'Invalid CSRF token'
                            && !(Array.isArray(result.files) && result.files.length > 0)) {
                            if (!/^[1-9]\d*$/.test(String(userId ?? ''))
                                || !Number.isSafeInteger(Number(userId))) {
                                throw new DocumentUploadOutcomeError('Sign in again before retrying this upload.', {
                                    status: 403, uploadOutcome: 'rejected', retryable: true,
                                });
                            }
                            let token;
                            try {
                                token = await ensureCaseDbCsrfToken({
                                    forceRefresh: true,
                                    expectedUserId: userId,
                                });
                            } catch (error) {
                                // Refresh has not re-sent the file; this request
                                // is still a confirmed pre-write rejection.
                                throw new DocumentUploadOutcomeError(
                                    'Your session could not be refreshed. Sign in again before retrying this upload.',
                                    { status: 403, uploadOutcome: 'rejected', retryable: true },
                                );
                            }
                            if (!token) {
                                throw new DocumentUploadOutcomeError(
                                    'Your session could not be refreshed. Sign in again before retrying this upload.',
                                    { status: 403, uploadOutcome: 'rejected', retryable: true },
                                );
                            }
                            upload.progress = 0;
                            upload.statusMessage = 'Retrying upload with refreshed security token...';
                            this.uploads.set(uploadId, upload);
                            this.notifyListeners();
                            uploadContext?.dispatch?.({
                                type: 'UPDATE_PROGRESS',
                                payload: {
                                    id: uploadId,
                                    progress: 0,
                                    statusMessage: upload.statusMessage,
                                    loadedBytes: 0,
                                    totalBytes: file.size,
                                    updatedAt: Date.now(),
                                },
                            });
                            sendAttempt(true, token);
                            return;
                        }

                        if (xhr.status === 200 && result.success) {
                            if ((skipExisting === true || result.skipped === true)
                                && (result.upload_conflict_policy_version !== UPLOAD_CONFLICT_POLICY_VERSION
                                    || (result.skipped !== undefined && typeof result.skipped !== 'boolean')
                                    || (result.skipped === true && (skipExisting !== true
                                        || result.skip_reason !== 'destination_exists'
                                        || !Array.isArray(result.files) || result.files.length !== 0)))) {
                                throw unknownDocumentUploadError('file', xhr.status);
                            }
                            upload.status = 'completed';
                            upload.progress = 100;
                            upload.skipped = result.skipped === true;
                            if (upload.skipped) upload.statusMessage = SKIPPED_UPLOAD_MESSAGE;
                            this.uploads.set(uploadId, upload);
                            this.notifyListeners();

                            // Mark as complete in global context
                            if (uploadContext?.dispatch) {
                                uploadContext.dispatch({
                                    type: 'COMPLETE_UPLOAD',
                                    payload: {
                                        id: uploadId,
                                        ...(upload.skipped ? { skipped: true, statusMessage: SKIPPED_UPLOAD_MESSAGE } : {}),
                                    }
                                });
                            }

                            resolve({ status: 'fulfilled', value: result });
                        } else {
                            throw documentUploadResponseError(result, xhr.status);
                        }
                    } catch (caught) {
                        const error = caught instanceof DocumentUploadOutcomeError
                            ? caught : unknownDocumentUploadError('file', xhr.status);
                        upload.status = 'error';
                        upload.error = error.message;
                        this.uploads.set(uploadId, upload);
                        this.notifyListeners();

                        // Mark as failed in global context
                        if (uploadContext?.dispatch) {
                            uploadContext.dispatch({
                                type: 'FAIL_UPLOAD',
                                payload: {
                                    id: uploadId,
                                    error: error.message
                                }
                            });
                        }

                        resolve({ status: 'rejected', reason: error });
                    }
                });

                const handleInterruptedRequest = () => {
                    const error = unknownDocumentUploadError('file', xhr.status);
                    upload.status = 'error';
                    upload.error = error.message;
                    this.uploads.set(uploadId, upload);
                    this.notifyListeners();

                    // Mark as failed in global context
                    if (uploadContext?.dispatch) {
                        uploadContext.dispatch({
                            type: 'FAIL_UPLOAD',
                            payload: {
                                id: uploadId,
                                error: error.message
                            }
                        });
                    }
                    resolve({ status: 'rejected', reason: error });
                };
                xhr.addEventListener('error', handleInterruptedRequest);
                xhr.addEventListener('timeout', handleInterruptedRequest);
                xhr.addEventListener('abort', handleInterruptedRequest);

                xhr.open('POST', 'https://api.casedb.co/s3_upload.php');
                xhr.withCredentials = true;
                // The global XHR interceptor (utils/csrf.js) adds X-CSRF-Token on
                // send(); setting it manually here too appends a duplicate value
                // and makes server-side validation fail with token-mismatch.
                xhr.timeout = 0; // Large uploads can legitimately take much longer than five minutes.
                if (csrfRetryAttempted && (!verifiedRetryToken || getCaseDbCsrfToken() !== verifiedRetryToken)) {
                    throw new DocumentUploadOutcomeError(
                        'Your session changed. Sign in again before retrying this upload.',
                        { status: 403, uploadOutcome: 'rejected', retryable: true },
                    );
                }
                xhr.send(formData);
            };
            sendAttempt();
        });
    }

    async sendDocumentTransferRequest(payload, options = {}) {
        const expectedUserId = /^[1-9]\d*$/.test(String(options.userId ?? ''))
            ? String(options.userId)
            : null;
        const requestBody = JSON.stringify(payload);
        const send = async (csrfRetryAttempted = false) => {
            const csrfToken = await ensureCaseDbCsrfToken({ expectedUserId });
            if (!csrfToken) {
                throw new Error('Your session could not be verified. Sign in again and retry.');
            }
            const response = await fetch(S3_UPLOAD_ENDPOINT, {
                method: 'PUT',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'PUT'),
                credentials: 'include',
                body: requestBody,
            });
            const result = await response.json().catch(() => ({
                success: false,
                message: 'The document action response could not be read',
            }));
            // This exact rejection is emitted before the endpoint dispatches
            // the operation. It is therefore safe to refresh the verified
            // same-user token and resend the byte-identical request once.
            const isExactPrewriteCsrfRejection = response.status === 403
                && result?.success === false
                && result?.message === 'Invalid CSRF token'
                && !result?.receipt_id
                && !result?.request_id;
            if (!csrfRetryAttempted && expectedUserId && isExactPrewriteCsrfRejection) {
                clearCaseDbCsrfToken();
                const refreshedToken = await ensureCaseDbCsrfToken({
                    forceRefresh: true,
                    expectedUserId,
                });
                if (refreshedToken) return send(true);
            }
            return { response, result };
        };
        return send();
    }

    async moveFiles(keys, targetFolder, caseId, uploadContext, renameMap = {}, displayFiles = null, options = {}) {
        if (!keys || keys.length === 0) {
            return { success: true, message: 'No files to move' };
        }

        const batchId = `batch-move-${Date.now()}`;
        try {
            const batchFiles = Array.isArray(displayFiles) && displayFiles.length > 0
                ? displayFiles
                : keys.map(k => ({ name: k.split('/').pop() || k }));

            // Track the move operation
            if (uploadContext?.dispatch) {
                uploadContext.dispatch({
                    type: 'START_BATCH',
                    payload: {
                        id: batchId,
                        operation: 'move',
                        operationLabel: 'Moving documents',
                        totalFiles: keys.length,
                        files: batchFiles,
                        statusMessage: 'Moving selected items',
                        createdAt: Date.now()
                    }
                });
            }

            const requestId = options.requestId || this.createDocumentTransferRequestId();
            const { response, result } = await this.sendDocumentTransferRequest({
                action: 'move',
                case_id: caseId,
                keys: keys,
                target_folder: targetFolder,
                rename_map: renameMap,
                request_id: requestId,
            }, options);
            if (result.success !== true) {
                result.success = false;
                result.message = result.message || (response.ok ? 'Move failed' : 'Move request failed');
            }

            // Complete the batch
            if (uploadContext?.dispatch) {
                uploadContext.dispatch({
                    type: result.success ? 'COMPLETE_UPLOAD' : 'FAIL_UPLOAD',
                    payload: {
                        id: batchId,
                        error: result.message || result.error
                    }
                });
            }

            return result;

        } catch (error) {
            console.error('Move files error:', error);
            const message = error.message || 'Move failed';
            if (uploadContext?.dispatch) {
                uploadContext.dispatch({
                    type: 'FAIL_UPLOAD',
                    payload: { id: batchId, error: message }
                });
            }
            return { success: false, error: message, message };
        }
    }

    async deleteFiles(keys, caseId, options = {}) {
        try {
            const expectedUserId = /^[1-9]\d*$/.test(String(options.userId ?? ''))
                ? String(options.userId)
                : null;
            const requestBody = JSON.stringify({
                case_id: caseId,
                keys: keys,
                bulk_objects: options.bulkObjects === true,
                ...(options.requestId ? { request_id: options.requestId } : {})
            });
            const sendDelete = async (csrfRetryAttempted = false) => {
                await ensureCaseDbCsrfToken({ expectedUserId });
                const response = await fetch(S3_UPLOAD_ENDPOINT, {
                    method: 'DELETE',
                    headers: withCsrfHeaders({
                        'Content-Type': 'application/json'
                    }, 'DELETE'),
                    credentials: 'include',
                    body: requestBody
                });
                const result = await response.json().catch(() => ({
                    success: false,
                    message: 'The delete response could not be read'
                }));

                // CSRF rejection happens before the delete state machine sees
                // the request. Refresh and resend exactly once with the same
                // UUID so a lost or delayed response can never become a second
                // logical deletion.
                const isExactPrewriteCsrfRejection = response.status === 403
                    && result?.success === false
                    && result?.message === 'Invalid CSRF token'
                    && !result?.undo
                    && !result?.receipt_id
                    && !result?.request_id;
                if (!csrfRetryAttempted && expectedUserId && isExactPrewriteCsrfRejection) {
                    clearCaseDbCsrfToken();
                    const refreshedToken = await ensureCaseDbCsrfToken({
                        forceRefresh: true,
                        expectedUserId,
                    });
                    if (refreshedToken) return sendDelete(true);
                }
                return result;
            };

            const result = await sendDelete();
            if (result?.success !== true) {
                return {
                    ...result,
                    success: false,
                    message: result?.message || result?.error || 'Delete request failed'
                };
            }
            return result;

        } catch (error) {
            console.error('Delete files error:', error);
            return { success: false, error: error.message };
        }
    }

    async deleteFilesWithProgress(keys, caseId, currentPath, uploadContext, options = {}) {
        if (!keys || keys.length === 0) {
            return { success: true, message: 'No files to delete' };
        }

        const caseName = options.caseName || null;
        const renderName = (raw) => {
            if (!raw) return raw;
            return raw.replace(/{{Name}}/g, caseName || 'Case documents');
        };
        const renderDeleteItemName = (key) => {
            const withoutTrailingSlash = String(key || '').replace(/\/+$/, '');
            const leafName = withoutTrailingSlash.split('/').pop();
            return renderName(leafName || 'Folder');
        };
        // One user confirmation maps to one idempotent server mutation and one
        // durable receipt. Folder enumeration may happen first, but the delete
        // itself must never be split into concurrent client-side mutations.
        const requestId = this.createDocumentDeleteRequestId();

        try {
            // STEP 1: First, count all files to determine if batching is needed
            console.log('Counting files before deletion...');
            await ensureCaseDbCsrfToken();
            const countResponse = await fetch(S3_UPLOAD_ENDPOINT, {
                method: 'DELETE',
                headers: withCsrfHeaders({
                    'Content-Type': 'application/json'
                }, 'DELETE'),
                credentials: 'include',
                body: JSON.stringify({
                    case_id: caseId,
                    keys: keys,
                    count_only: true
                })
            });

            const countResult = await countResponse.json().catch(() => ({
                success: false,
                message: 'The delete preview response could not be read'
            }));
            
            if (!countResponse.ok || !countResult.success) {
                throw new Error(countResult.message || 'Failed to count files');
            }

            console.log(`Found ${countResult.total_files} files (${(countResult.total_size / 1024 / 1024).toFixed(2)} MB)`);
            
            // STEP 2: Submit exactly one mutation, even when the server says
            // the selection is large. The server owns internal S3 chunking so
            // the whole confirmed action can receive one receipt and undo as a
            // unit instead of becoming four racing browser requests.
            // Preserve the original user-selected boundaries. Returning the
            // count preview's expanded object inventory would turn one folder
            // into hundreds of client-authored scopes and can exceed receipt
            // limits. The server owns exact enumeration and journaling.
            const selectedKeys = Array.from(new Set(keys));
            const batchId = `batch-delete-${Date.now()}`;
            if (uploadContext?.dispatch) {
                uploadContext.dispatch({
                    type: 'START_BATCH',
                    payload: {
                        id: batchId,
                        operation: 'delete',
                        operationLabel: 'Deleting documents',
                        totalFiles: Number(countResult.total_files) || selectedKeys.length,
                        files: selectedKeys.map(key => ({
                            name: renderDeleteItemName(key)
                        })),
                        statusMessage: 'Deleting selected items',
                        createdAt: Date.now()
                    }
                });
            }

            const result = await this.deleteBatch(selectedKeys, caseId, {
                bulkObjects: false,
                requestId,
                ...(options.userId ? { userId: options.userId } : {}),
            });
            const logicalResult = {
                ...result,
                totalCount: Number(countResult.total_files) || result.totalCount,
            };
            if (uploadContext?.dispatch) {
                uploadContext.dispatch({
                    type: logicalResult.success ? 'COMPLETE_UPLOAD' : 'FAIL_UPLOAD',
                    payload: {
                        id: batchId,
                        error: logicalResult.success ? null : logicalResult.message
                    }
                });
            }
            return logicalResult;
        } catch (error) {
            console.error('Delete operation failed:', error);
            return {
                success: false,
                message: error.message
            };
        }
    }

    async deleteBatch(keys, caseId, options = {}) {
        const result = await this.deleteFiles(keys, caseId, {
            bulkObjects: options.bulkObjects === true,
            requestId: options.requestId,
            ...(options.userId ? { userId: options.userId } : {}),
        });
        const successful = Number(
            result.successful_count
            ?? result.deleted_count
            ?? result.deleted?.length
            ?? (result.success ? keys.length : 0)
        );
        const failed = Number(
            result.failed_count
            ?? result.errors?.length
            ?? (result.success ? 0 : Math.max(0, keys.length - successful))
        );

        return {
            ...result,
            success: result.success && failed === 0,
            totalCount: keys.length,
            successful,
            failed,
            message: failed === 0
                ? `Successfully deleted ${successful} files`
                : (result.message || `Deleted ${successful} files, ${failed} failed`)
        };
    }

    async copyFilesWithProgress(sourceKeys, caseId, targetFolder, uploadContext, renameMap = {}, options = {}) {
        if (!sourceKeys || sourceKeys.length === 0) {
            return { success: true, message: 'No files to copy' };
        }

        const batchId = `batch-copy-${Date.now()}`;
        
        // Track the copy operation as a batch
        if (uploadContext?.dispatch) {
            uploadContext.dispatch({
                type: 'START_BATCH',
                payload: {
                    id: batchId,
                    operation: 'copy',
                    operationLabel: 'Copying documents',
                    totalFiles: sourceKeys.length,
                    files: sourceKeys.map(k => ({ name: k.split('/').filter(Boolean).pop() || k })),
                    statusMessage: 'Copying selected items',
                    createdAt: Date.now()
                }
            });
        }

        try {
            // One server request keeps a multi-item copy and its exact undo
            // receipt atomic. The tray remains at 0% while S3 work is
            // indeterminate, then moves to 100% only after the server confirms.
            const requestId = options.requestId || this.createDocumentTransferRequestId();
            const { response, result } = await this.sendDocumentTransferRequest({
                action: 'copy',
                case_id: caseId,
                source_keys: sourceKeys,
                target_folder: targetFolder,
                rename_map: renameMap,
                request_id: requestId,
            }, options);
            if (!response.ok && result.success !== false) {
                result.success = false;
                result.message = result.message || 'Copy request failed';
            }

            if (uploadContext?.dispatch) {
                uploadContext.dispatch({
                    type: result.success ? 'COMPLETE_UPLOAD' : 'FAIL_UPLOAD',
                    payload: {
                        id: batchId,
                        error: result.message || result.error || 'Copy failed'
                    }
                });
            }

            const copiedRows = Array.isArray(result.copied)
                ? result.copied
                : (result.copied ? [result.copied] : []);
            return {
                ...result,
                totalCount: sourceKeys.length,
                successful: result.success ? sourceKeys.length : copiedRows.length,
                failed: result.success ? 0 : Math.max(1, sourceKeys.length - copiedRows.length)
            };
        } catch (error) {
            console.error('Copy files error:', error);
            if (uploadContext?.dispatch) {
                uploadContext.dispatch({
                    type: 'FAIL_UPLOAD',
                    payload: { id: batchId, error: error.message || 'Copy failed' }
                });
            }
            return { success: false, error: error.message, message: error.message };
        }
    }

    async copySingleFile(sourceKey, fileName, caseId, targetFolder, renameMap = {}, onProgress, options = {}) {
        const copyId = `copy-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        
        const copyOp = {
            id: copyId,
            fileName: `${fileName}`,
            status: 'uploading', // Reuse uploading status for consistency
            progress: 0,
            error: null,
            fileSize: 0, // Unknown size for copies
            createdAt: Date.now(),
            isCopy: true
        };

        this.uploads.set(copyId, copyOp);
        this.notifyListeners();

        try {
            // Start progress
            copyOp.progress = 50;
            this.uploads.set(copyId, copyOp);
            this.notifyListeners();
            
            if (onProgress) onProgress(50);

            const requestId = options.requestId || this.createDocumentTransferRequestId();
            const { result } = await this.sendDocumentTransferRequest({
                action: 'copy',
                case_id: caseId,
                source_keys: [sourceKey],
                target_folder: targetFolder,
                rename_map: renameMap,
                request_id: requestId,
            }, options);

            if (result.success) {
                copyOp.status = 'completed';
                copyOp.progress = 100;
                this.uploads.set(copyId, copyOp);
                this.notifyListeners();
                
                if (onProgress) onProgress(100);
                
                return { success: true, result };
            } else {
                throw new Error(result.message || 'Copy failed');
            }

        } catch (error) {
            copyOp.status = 'error';
            copyOp.error = error.message;
            this.uploads.set(copyId, copyOp);
            this.notifyListeners();
            
            return { success: false, error: error.message };
        }
    }

    renameFile(oldKey, newName, caseId, isFolder = false, uploadContext, options = {}) {
        if (!isFolder) {
            return this.performRename(oldKey, newName, caseId, false, uploadContext, options);
        }

        const operationKey = `${String(caseId)}:${String(oldKey)}`;
        const existingPromise = this.folderRenamePromises.get(operationKey);
        if (existingPromise) {
            return existingPromise;
        }

        const renamePromise = this.performRename(
            oldKey,
            newName,
            caseId,
            true,
            uploadContext,
            options
        ).finally(() => {
            if (this.folderRenamePromises.get(operationKey) === renamePromise) {
                this.folderRenamePromises.delete(operationKey);
            }
        });

        this.folderRenamePromises.set(operationKey, renamePromise);
        return renamePromise;
    }

    async performRename(oldKey, newName, caseId, isFolder, uploadContext, options = {}) {
        const requestId = isFolder ? this.createFolderRenameRequestId() : null;
        const normalizedOldKey = String(oldKey || '').replace(/\/+$/, '');
        const fileName = normalizedOldKey.split('/').pop() || normalizedOldKey || 'Item';
        const renameId = isFolder
            ? `rename-${requestId}`
            : `rename-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

        try {
            // Track the rename operation
            if (uploadContext?.dispatch) {
                uploadContext.dispatch({
                    type: 'START_UPLOAD',
                    payload: {
                        id: renameId,
                        fileName: `${fileName} → ${newName}`,
                        fileSize: 0,
                        operation: 'rename',
                        statusMessage: isFolder ? 'Requesting folder rename...' : 'Renaming...'
                    }
                });
            }

            // Update progress
            if (uploadContext?.dispatch) {
                uploadContext.dispatch({
                    type: 'UPDATE_PROGRESS',
                    payload: {
                        id: renameId,
                        progress: isFolder ? 5 : 50,
                        statusMessage: isFolder ? 'Requesting folder rename...' : 'Renaming...'
                    }
                });
            }

            await ensureCaseDbCsrfToken();
            const response = await fetch(S3_UPLOAD_ENDPOINT, {
                method: 'PUT',
                headers: withCsrfHeaders({
                    'Content-Type': 'application/json'
                }, 'PUT'),
                credentials: 'include',
                body: JSON.stringify({
                    action: 'rename',
                    case_id: caseId,
                    old_key: oldKey,
                    new_name: newName,
                    is_folder: isFolder,
                    ...(requestId ? { request_id: requestId } : {})
                })
            });

            const result = await this.parseApiResponse(response, 'Rename request');
            if (!result.success) {
                throw new UploadApiError(
                    result.error || result.message || 'The rename request was not accepted.',
                    response.status
                );
            }

            const isQueuedFolderRename = isFolder && (
                response.status === 202 ||
                result.queued === true ||
                Boolean(result.job_id && result.status_url)
            );

            if (isQueuedFolderRename) {
                if (!result.job_id) {
                    throw new UploadApiError('The folder rename was queued without a job identifier.', response.status);
                }

                const acceptedJob = result.job || result.rename_job || result;
                const acceptedStatus = String(acceptedJob.status || result.status || 'queued').trim().toLowerCase();
                this.dispatchRenameProgress(
                    uploadContext,
                    renameId,
                    10,
                    'Folder rename queued',
                    options,
                    this.folderRenameReservationMetadata(result, acceptedJob, acceptedStatus)
                );
                if (typeof options.onAccepted === 'function') {
                    options.onAccepted(result);
                }

                const completedResult = await this.pollFolderRename(result, renameId, uploadContext, options);
                this.completeRenameUpload(uploadContext, renameId, completedResult);
                return completedResult;
            }

            this.completeRenameUpload(uploadContext, renameId, result);

            return result;

        } catch (error) {
            console.error('Rename file error:', error);
            const message = error?.message || 'The rename could not be completed.';
            this.failRenameUpload(uploadContext, renameId, message);
            return { success: false, error: message, message };
        }
    }

    createFolderRenameRequestId() {
        if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
            return window.crypto.randomUUID();
        }
        return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    }

    createDocumentDeleteRequestId() {
        const cryptoApi = typeof window !== 'undefined' ? window.crypto : null;
        if (typeof cryptoApi?.randomUUID === 'function') {
            return cryptoApi.randomUUID();
        }
        const bytes = new Uint8Array(16);
        if (typeof cryptoApi?.getRandomValues === 'function') {
            cryptoApi.getRandomValues(bytes);
        } else {
            for (let index = 0; index < bytes.length; index += 1) {
                bytes[index] = Math.floor(Math.random() * 256);
            }
        }
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0'));
        return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
    }

    createDocumentTransferRequestId() {
        return this.createDocumentDeleteRequestId();
    }

    async parseApiResponse(response, actionLabel) {
        const bodyText = await response.text();
        let result = null;

        if (bodyText.trim()) {
            try {
                result = JSON.parse(bodyText);
            } catch (error) {
                result = null;
            }
        }

        if (!response.ok) {
            const statusLabel = [response.status, response.statusText].filter(Boolean).join(' ');
            const htmlTitle = bodyText.match(/<title[^>]*>([^<]+)<\/title>/i)?.[1]?.trim();
            const detail = result?.message || result?.error || htmlTitle || statusLabel || 'Server error';
            throw new UploadApiError(`${actionLabel} failed: ${detail}`, response.status);
        }

        if (!result || typeof result !== 'object') {
            throw new UploadApiError(`${actionLabel} returned an unreadable response.`, response.status);
        }

        return result;
    }

    resolveFolderRenameStatusUrl(initialResult) {
        const fallback = `${S3_UPLOAD_ENDPOINT}?action=rename_status&job_id=${encodeURIComponent(initialResult.job_id)}`;
        if (!initialResult.status_url) return fallback;

        try {
            const url = new URL(initialResult.status_url, S3_UPLOAD_ENDPOINT);
            const expectedOrigin = new URL(S3_UPLOAD_ENDPOINT).origin;
            return url.origin === expectedOrigin ? url.toString() : fallback;
        } catch (error) {
            return fallback;
        }
    }

    async pollFolderRename(initialResult, renameId, uploadContext, options = {}) {
        const statusUrl = this.resolveFolderRenameStatusUrl(initialResult);

        while (true) {
            if (this.FOLDER_RENAME_POLL_INTERVAL_MS > 0) {
                await new Promise(resolve => setTimeout(resolve, this.FOLDER_RENAME_POLL_INTERVAL_MS));
            }

            let statusResult;
            try {
                const response = await fetch(statusUrl, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Accept': 'application/json' },
                    cache: 'no-store'
                });
                statusResult = await this.parseApiResponse(response, 'Folder rename status');
            } catch (error) {
                if (error?.status && error.status < 500) {
                    throw error;
                }
                this.dispatchRenameProgress(
                    uploadContext,
                    renameId,
                    undefined,
                    'Waiting for folder rename status...',
                    options,
                    {
                        status: 'waiting',
                        phase: 'status',
                        waiting: true,
                        processing: false
                    }
                );
                continue;
            }

            const job = statusResult.job || statusResult.rename_job || statusResult;
            const status = String(job.status || statusResult.status || '').trim().toLowerCase();
            if (!status) {
                throw new UploadApiError('Folder rename status response was incomplete.');
            }

            const isCompleted = ['completed', 'complete', 'succeeded', 'done'].includes(status);
            const progress = isCompleted ? 100 : this.folderRenameProgress(job, status);
            const statusMessage = job.status_message
                || job.message
                || (isCompleted ? 'Folder rename complete' : this.folderRenameStatusMessage(status));
            this.dispatchRenameProgress(
                uploadContext,
                renameId,
                progress,
                statusMessage,
                options,
                this.folderRenameReservationMetadata(statusResult, job, status)
            );

            if (isCompleted) {
                return {
                    ...initialResult,
                    ...statusResult,
                    job,
                    success: true,
                    queued: true,
                    completed: true
                };
            }

            if (FOLDER_RENAME_TERMINAL_FAILURES.has(status) || statusResult.success === false) {
                throw new UploadApiError(
                    job.public_error || job.error || job.error_message ||
                    statusResult.message || statusResult.error ||
                    'The folder rename could not be completed.'
                );
            }
        }
    }

    folderRenameProgress(job, status) {
        const explicitProgress = Number(job.progress ?? job.progress_percent);
        if (Number.isFinite(explicitProgress)) {
            return Math.max(10, Math.min(99, explicitProgress));
        }

        const total = Number(job.object_total ?? job.objects_total ?? job.total_objects);
        const copied = Number(job.object_copied ?? job.objects_copied ?? job.copied_objects);
        const deleted = Number(job.object_deleted ?? job.objects_deleted ?? job.deleted_objects);
        if (Number.isFinite(total) && total > 0) {
            if (Number.isFinite(deleted) && deleted > 0) {
                return Math.min(99, 85 + Math.round((deleted / total) * 14));
            }
            if (Number.isFinite(copied) && copied >= 0) {
                return Math.min(75, 15 + Math.round((copied / total) * 60));
            }
        }

        const phaseProgress = {
            queued: 10,
            claimed: 12,
            inventory: 15,
            inventorying: 15,
            copying: 35,
            verifying: 75,
            database: 82,
            cutover: 82,
            deleting: 90,
            finalizing: 98,
            retry: 10
        };
        return phaseProgress[status] ?? 10;
    }

    folderRenameStatusMessage(status) {
        const messages = {
            queued: 'Folder rename queued',
            claimed: 'Preparing folder rename...',
            inventory: 'Checking folder contents...',
            inventorying: 'Checking folder contents...',
            copying: 'Copying folder contents...',
            verifying: 'Verifying renamed folder...',
            database: 'Updating CaseDB records...',
            cutover: 'Updating CaseDB records...',
            deleting: 'Removing the old folder...',
            finalizing: 'Finishing folder rename...',
            retry: 'Folder rename will retry shortly...'
        };
        return messages[status] || 'Renaming folder...';
    }

    folderRenameReservationMetadata(statusResult, job, status) {
        const readOptionalBoolean = (...values) => {
            for (const value of values) {
                if (typeof value === 'boolean') return value;
                if (value === 1 || value === '1') return true;
                if (value === 0 || value === '0') return false;
            }
            return undefined;
        };
        const pathsReserved = readOptionalBoolean(
            job?.paths_reserved,
            statusResult?.paths_reserved
        );
        const sourceAdditionsAllowed = readOptionalBoolean(
            job?.source_additions_allowed,
            statusResult?.source_additions_allowed
        );
        const explicitWaiting = readOptionalBoolean(job?.waiting, statusResult?.waiting);
        const explicitProcessing = readOptionalBoolean(job?.processing, statusResult?.processing);
        const fallbackWaiting = ['queued', 'waiting', 'retry'].includes(status);

        return {
            status,
            phase: String(job?.phase || statusResult?.phase || status || '').trim().toLowerCase(),
            waiting: explicitWaiting ?? fallbackWaiting,
            processing: explicitProcessing ?? !fallbackWaiting,
            ...(typeof pathsReserved === 'boolean' ? { pathsReserved } : {}),
            ...(typeof sourceAdditionsAllowed === 'boolean' ? { sourceAdditionsAllowed } : {})
        };
    }

    dispatchRenameProgress(uploadContext, renameId, progress, statusMessage, options = {}, metadata = {}) {
        if (typeof options.onProgress === 'function') {
            options.onProgress({ progress, statusMessage, ...metadata });
        }
        if (!uploadContext?.dispatch) return;
        uploadContext.dispatch({
            type: 'UPDATE_PROGRESS',
            payload: {
                id: renameId,
                ...(Number.isFinite(progress) ? { progress } : {}),
                statusMessage,
                ...(metadata.status ? { renameStatus: metadata.status } : {}),
                ...(metadata.phase ? { renamePhase: metadata.phase } : {}),
                ...(typeof metadata.pathsReserved === 'boolean' ? { pathsReserved: metadata.pathsReserved } : {}),
                ...(typeof metadata.sourceAdditionsAllowed === 'boolean'
                    ? { sourceAdditionsAllowed: metadata.sourceAdditionsAllowed }
                    : {}),
                updatedAt: Date.now()
            }
        });
    }

    completeRenameUpload(uploadContext, renameId) {
        if (!uploadContext?.dispatch) return;
        uploadContext.dispatch({ type: 'COMPLETE_UPLOAD', payload: { id: renameId } });
    }

    failRenameUpload(uploadContext, renameId, message) {
        if (!uploadContext?.dispatch) return;
        uploadContext.dispatch({
            type: 'FAIL_UPLOAD',
            payload: { id: renameId, error: message }
        });
    }

    async createFolder(caseId, folderPath, userId) {
        try {
            await ensureCaseDbCsrfToken();
            const response = await fetch('https://api.casedb.co/s3_upload.php', {
                method: 'POST',
                headers: withCsrfHeaders({
                    'Content-Type': 'application/json'
                }, 'POST'),
                credentials: 'include',
                body: JSON.stringify({
                    action: 'create_folder',
                    case_id: caseId,
                    folder_path: folderPath,
                    user_id: userId
                })
            });

            let result;
            try { result = await response.json(); }
            catch (_) { throw unknownDocumentUploadError('folder', response.status); }
            if (!result || typeof result !== 'object' || Array.isArray(result)
                || typeof result.success !== 'boolean') {
                throw unknownDocumentUploadError('folder', response.status);
            }
            if (result.success !== true || response.ok === false) {
                throw documentUploadResponseError(result, response.status, 'folder');
            }
            return result;

        } catch (error) {
            const outcome = error instanceof DocumentUploadOutcomeError
                ? error : unknownDocumentUploadError('folder');
            console.error('Create folder error:', outcome.message);
            return {
                success: false,
                message: outcome.message,
                error: outcome.message,
                uploadOutcome: outcome.uploadOutcome,
                retryable: outcome.retryable,
            };
        }
    }

    getUploads() {
        return Array.from(this.uploads.values());
    }

    removeUpload(uploadId) {
        this.uploads.delete(uploadId);
        this.notifyListeners();
    }

    clearCompleted() {
        const completed = Array.from(this.uploads.entries())
            .filter(([_, upload]) => upload.status === 'completed')
            .map(([id]) => id);
        
        completed.forEach(id => this.uploads.delete(id));
        this.notifyListeners();
    }

    // Check if all uploads are finished (completed or error)
    areAllUploadsFinished() {
        const uploads = Array.from(this.uploads.values());
        return uploads.length === 0 || uploads.every(upload => 
            upload.status === 'completed' || upload.status === 'error'
        );
    }

    // Get upload statistics
    getUploadStats() {
        const uploads = Array.from(this.uploads.values());
        return {
            total: uploads.length,
            completed: uploads.filter(u => u.status === 'completed').length,
            failed: uploads.filter(u => u.status === 'error').length,
            uploading: uploads.filter(u => u.status === 'uploading').length,
            allFinished: this.areAllUploadsFinished()
        };
    }

    // Clear all uploads (use when closing upload helper)
    clearAll() {
        this.uploads.clear();
        this.notifyListeners();
    }

    // Method for external compatibility with existing code
    registerExternalUpload(fileName, fileSize) {
        const id = `ext-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const upload = {
            id,
            fileName,
            status: 'uploading',
            progress: 0,
            error: null,
            fileSize,
            createdAt: Date.now(),
            external: true
        };
        this.uploads.set(id, upload);
        this.notifyListeners();
        return id;
    }

    updateExternalUpload(id, progress, status = 'uploading') {
        const upload = this.uploads.get(id);
        if (upload) {
            upload.progress = progress;
            upload.status = status;
            this.uploads.set(id, upload);
            this.notifyListeners();
        }
    }
}

// Create a singleton instance
const uploadService = new SimpleUploadService();

export default uploadService;
